import { type } from "os";
import { BaseRecord } from "./BaseRecord";
import { BaseRootRepository } from "./repository/BaseRootRepository";
import { RecordRepository } from "./repository/RecordRepository";
import { DateTime } from "utils/dateTime";

export class TournamentDatasourceIdentifierRecord<TJsonData extends TournamentDatasourceIdentifierRecord.CtorJsonData = TournamentDatasourceIdentifierRecord.CtorJsonData>
extends BaseRecord<TJsonData> {
  tournament_id?: string
  identifier?: string
  identifier_kind?: string
  is_enabled?: boolean | null
  admin_note?: string
  original_data?: string
  is_primary?: boolean | null
  last_data_change_at?: Date | null

  static createNew(db?:BaseRootRepository.OrNothing, jsonData?:TournamentDatasourceIdentifierRecord.CtorJsonData) {
    return new TournamentDatasourceIdentifierRecord(db, jsonData)
  }

  constructor(db?:BaseRootRepository.OrNothing, jsonData?:TJsonData) {
    super(db);

    if(jsonData) {
      this.patchData(jsonData)
    }
  }

  patchData(jsonData: TJsonData) {
    super.patchData(jsonData)

    this.tournament_id = jsonData?.tournament_id
    this.identifier = jsonData?.identifier
    this.identifier_kind = jsonData?.identifier_kind
    this.is_enabled = jsonData?.is_enabled
    this.admin_note = jsonData?.admin_note
    this.original_data = jsonData?.original_data
    this.is_primary = jsonData?.is_primary
    this.last_data_change_at = DateTime.parseDateOrNull(jsonData?.last_data_change_at)
  }

  toJson():TJsonData {
    return Object.assign(super.toJson(), {
      tournament_id: this.tournament_id,
      identifier: this.identifier,
      identifier_kind: this.identifier_kind,
      is_enabled: this.is_enabled,
      admin_note: this.admin_note,
      original_data: this.original_data,
      is_primary: this.is_primary,
      last_data_change_at: this.last_data_change_at?.toISOString()
    }) as TJsonData
  }
}

export module TournamentDatasourceIdentifierRecord {
  export type CtorJsonData = BaseRecord.CtorJsonData & {
    tournament_id?: string
    identifier?: string
    identifier_kind?: string
    is_enabled?: boolean | undefined
    priority?: number
    admin_note?: string
    original_data?: string
    is_primary?: boolean | null
    last_data_change_at?: string
  }

  export class Repository extends RecordRepository<TournamentDatasourceIdentifierRecord> {
    #getAll:RecordRepository.GetFieldFlags = {}
    getAll(options?:RecordRepository.GetOptions) {
      return this.peekOrLoad(this.#getAll, options, {
        peek: () => this.peekAll(),
        load: () => this.loadAll()
      })
    }

    /// >>> by tournament_id >>>
    #getByTournamentId:RecordRepository.NestingGetFieldFlags = {}

    getByTournamentId(tournamentId:string, options?:RecordRepository.GetOptions) {
      return this.nestedPeekOrLoad(this.#getByTournamentId, tournamentId, options, {
        peek: () => this.peekByTournamentId(tournamentId),
        load: () => this.loadByTournamentId(tournamentId)
      })
    }

    peekByTournamentId(tournamentId:string) {
      return this.peekAll(x => x?.tournament_id == tournamentId)
    }

    protected loadByTournamentId(tournamentId:string) {
      return this.getApi().getMoreTournamentDatasourceIdentifiersByTournamentId(tournamentId)
    }
    /// <<< by tournament_id <<<

    create(record: Partial<TournamentDatasourceIdentifierRecord<CtorJsonData>>): Promise<TournamentDatasourceIdentifierRecord<CtorJsonData> | null> {
      const api = this.getApiOrThrow()
      return api.createOneTournamentDatasourceIdentifier(record)
    }

    protected loadAll() {
      return this.getApi().getAllTournamentDatasourceIdentifiers()
    }

    protected loadById(id: string): Promise<TournamentDatasourceIdentifierRecord<CtorJsonData> | null> {
      return this.getApi().getOneTournamentDatasourceIdentifierById(id)
    }
    protected updateById(id: string, patch: Partial<TournamentDatasourceIdentifierRecord<CtorJsonData>>): Promise<TournamentDatasourceIdentifierRecord<CtorJsonData> | null> {
      const api = this.getApiOrThrow()
      return api.updateOneTournamentDatasourceIdentifier(id, patch)
    }
    protected deleteById(id: string): Promise<TournamentDatasourceIdentifierRecord<CtorJsonData> | null> {
      const api = this.getApiOrThrow()
      return api.deleteOneTournamentDatasourceIdentifier(id)
    }
    protected approveById(id: string, patch: Partial<TournamentDatasourceIdentifierRecord<CtorJsonData>>): Promise<TournamentDatasourceIdentifierRecord<CtorJsonData> | null> {
      throw new Error("Method not implemented.");
    }
    protected unapproveById(id: string, patch: Partial<TournamentDatasourceIdentifierRecord<CtorJsonData>>): Promise<TournamentDatasourceIdentifierRecord<CtorJsonData> | null> {
      throw new Error("Method not implemented.");
    }
    protected publishById(id: string, patch: Partial<TournamentDatasourceIdentifierRecord<CtorJsonData>>): Promise<TournamentDatasourceIdentifierRecord<CtorJsonData> | null> {
      throw new Error("Method not implemented.");
    }
    protected unpublishById(id: string, patch: Partial<TournamentDatasourceIdentifierRecord<CtorJsonData>>): Promise<TournamentDatasourceIdentifierRecord<CtorJsonData> | null> {
      throw new Error("Method not implemented.");
    }
    protected freezeById(id: string, patch: Partial<TournamentDatasourceIdentifierRecord<CtorJsonData>>): Promise<TournamentDatasourceIdentifierRecord<CtorJsonData> | null> {
      throw new Error("Method not implemented.");
    }
    protected unfreezeById(id: string, patch: Partial<TournamentDatasourceIdentifierRecord<CtorJsonData>>): Promise<TournamentDatasourceIdentifierRecord<CtorJsonData> | null> {
      throw new Error("Method not implemented.");
    }
    protected touchById(id: string): Promise<number | null> {
      throw new Error("Method not implemented.");
    }
    protected touchAll_(): Promise<number | null> {
      throw new Error("Method not implemented.");
    }

  }
}
